<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 auth-background">
    <div class="max-w-md w-full">
      <div class="glassmorphic-card">
        <div class="text-center mb-8">
          <router-link to="/" class="inline-block mb-4">
            <img
              src="/images/venmas-logo-1.png"
              alt="Venmas"
              class="h-16 mx-auto object-contain"
            />
          </router-link>
          <h2 class="text-3xl font-bold text-white mb-2">
            {{ token ? 'Verifying email' : 'Check your email' }}
          </h2>
          <p class="text-white/70">
            {{ headingCopy }}
          </p>
        </div>

        <div class="space-y-6">
          <div v-if="error" class="p-4 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm text-center">
            {{ error }}
          </div>

          <div v-if="success" class="p-4 bg-green-500/20 border border-green-400/30 rounded-lg text-green-200 text-sm text-center">
            {{ success }}
          </div>

          <form v-if="!token && !verified" class="space-y-4" @submit.prevent="handleResend">
            <div>
              <label for="email" class="block text-sm font-semibold text-white/90 mb-2">Email Address</label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                autocomplete="email"
                class="glass-input"
                placeholder="you@example.com"
              />
            </div>

            <button
              type="submit"
              :disabled="authStore.loading || !email"
              class="w-full py-3 px-4 text-sm font-medium rounded-xl text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style="background: #2563eb;"
            >
              {{ authStore.loading ? 'Sending...' : 'Resend verification email' }}
            </button>
          </form>

          <router-link
            v-if="verified"
            to="/login"
            class="block w-full text-center py-3 px-4 text-sm font-medium rounded-xl text-white transition-all"
            style="background: #2563eb;"
          >
            Continue to sign in
          </router-link>

          <div class="text-center">
            <router-link to="/login" class="text-white/80 hover:text-white text-sm transition-colors">
              Back to sign in
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const authStore = useAuthStore()

const token = computed(() => {
  const value = route.params.token
  return typeof value === 'string' ? value : ''
})
const email = ref(typeof route.query.email === 'string' ? route.query.email : '')
const error = ref('')
const success = ref('')
const verified = ref(false)
const verifying = ref(false)

const headingCopy = computed(() => {
  if (verified.value) return 'Your email is verified. You can now sign in.'
  if (token.value) return 'Please wait while we confirm your account.'
  return email.value
    ? `We sent a verification link to ${email.value}.`
    : 'Enter your email to resend the verification link.'
})

function verifiedTokenStorageKey(value: string): string {
  return `venmas:verified-email-token:${value}`
}

async function verifyToken() {
  const currentToken = token.value
  if (!currentToken || verifying.value || verified.value) {
    return
  }

  const storageKey = verifiedTokenStorageKey(currentToken)
  if (sessionStorage.getItem(storageKey) === 'success') {
    verified.value = true
    success.value = 'Email verified successfully. You can now sign in.'
    error.value = ''
    return
  }

  verifying.value = true
  error.value = ''
  success.value = ''

  try {
    const response = await authStore.verifyEmail(currentToken)
    const responseMessage = (response as { message?: unknown })?.message
    success.value = typeof responseMessage === 'string' ? responseMessage : 'Email verified successfully. You can now sign in.'
    verified.value = true
    sessionStorage.setItem(storageKey, 'success')
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value = typeof apiError === 'string' ? apiError : 'Email verification failed. Please request a new verification link.'
  } finally {
    verifying.value = false
  }
}

async function handleResend() {
  error.value = ''
  success.value = ''

  try {
    const response = await authStore.resendVerificationEmail(email.value)
    const responseMessage = (response as { message?: unknown })?.message
    success.value = typeof responseMessage === 'string' ? responseMessage : 'Verification email sent if the account exists.'
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value = typeof apiError === 'string' ? apiError : 'Unable to resend verification email.'
  }
}

onMounted(() => {
  if (token.value) {
    verifyToken()
  }
})
</script>

<style scoped>
.auth-background {
  background: #1e3a5f url('/images/auth-bg.png') center / cover no-repeat fixed;
  position: relative;
  isolation: isolate;
}

.auth-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

.auth-background > * {
  position: relative;
  z-index: 1;
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  padding: 2rem;
  transition: all 0.3s ease;
}

.glass-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.glass-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

button[type="submit"]:hover,
a[style]:hover {
  background: #1d4ed8 !important;
}
</style>
